import { useEffect, useState } from 'react';
import { generateFormField } from '../../../utils/form/generate-form-fields/generate-form-fields';
import { errorMessages } from '../../../utils/error-messages';
import { ConfigurationFromPdfFieldNames } from '../configuration-from-pdf.types';
import cloneDeep from 'lodash/cloneDeep';
import { UseFormReturn } from 'react-hook-form';
import { getEntityById } from '../../../services/crm-entity-service';
import useContactsByCriteria from '../../../hooks/react-query/use-contacts-by-criteria';
import { USER_ROLES } from '../../../constants';
import { useTypedSelector } from '../../../hooks/use-typed-selector';
import { User } from '../../../common/interfaces';
import { GenerateFormFieldReturn } from '../../../utils/form/generate-form-fields/generate-form-fields.props';

const useResponsiblePerson = (isPoolcar: boolean, isReplacementCar: boolean, reactHookFormData: UseFormReturn) => {
  const user = useTypedSelector<User>(state => state['app'].user);
  const { setValue, clearErrors, getValues } = reactHookFormData;

  const [responsiblePersonFields, setResponsiblePersonFields] = useState<GenerateFormFieldReturn[]>([
    generateFormField.lookup({
      label: 'KST-Verantwortlicher',
      name: ConfigurationFromPdfFieldNames.ResponsiblePerson,
      disabled: false,
      options: undefined,
      validation: {}
    })
  ]);

  const { data: fleetManagersAndPoolManagers } = useContactsByCriteria({
    activeOnly: true,
    roleIds: [USER_ROLES.fleetManager.id, USER_ROLES.poolManager.id],
    accountId: user.companyId,
    includeChildAccounts: true,
    includeParentAccount: true,
    columns: ['contactid', 'fullname']
  });

  const oldCarValue = getValues(ConfigurationFromPdfFieldNames.OldCar);
  const responsiblePersonValue = getValues(ConfigurationFromPdfFieldNames.ResponsiblePerson);

  useEffect(() => {
    if (fleetManagersAndPoolManagers) {
      const newResponsiblePersonFields = cloneDeep(responsiblePersonFields);
      newResponsiblePersonFields[0].options = fleetManagersAndPoolManagers.map(item => ({
        value: item.contactid,
        label: item.fullname
      }));
      setResponsiblePersonFields(newResponsiblePersonFields);
    }
  }, [!!fleetManagersAndPoolManagers]);

  useEffect(() => {
    if (isReplacementCar) {
      fillResponsiblePerson(null);
    }
  }, [isReplacementCar]);

  useEffect(() => {
    fillResponsiblePerson(null);
  }, [isPoolcar]);

  useEffect(() => {
    fillResponsiblePerson(oldCarValue);
  }, [oldCarValue]);

  const updateFields = (oldCarId: string | null, responsiblePersonId: string) => {
    const newResponsiblePersonFields = cloneDeep(responsiblePersonFields);
    newResponsiblePersonFields[0].validation.required = isPoolcar ? errorMessages.requiredOptionSet : false;
    newResponsiblePersonFields[0].disabled = (!!oldCarId || !!oldCarValue) && !!responsiblePersonId;
    setResponsiblePersonFields(newResponsiblePersonFields);
  };

  const fillResponsiblePerson = (value: string | null) => {
    if (!responsiblePersonFields?.[0]?.options) {
      return;
    }

    if (value) {
      getEntityById({
        entityId: value,
        entityName: 'new_fuhrpark',
        columns: ['new_kstverantw']
      }).then(response => {
        let responsiblePersonId = response.data.data.attributes.new_kstverantw.attributeValue?.id;
        if (!responsiblePersonFields[0].options?.some(option => option.value === responsiblePersonId)) {
          responsiblePersonId = '';
        }
        updateFields(value, responsiblePersonId);
        setValue(ConfigurationFromPdfFieldNames.ResponsiblePerson, responsiblePersonId || '');
        clearErrors(ConfigurationFromPdfFieldNames.ResponsiblePerson);
      });
    } else {
      updateFields(value, responsiblePersonValue);
      clearErrors(ConfigurationFromPdfFieldNames.ResponsiblePerson);
    }
  };

  return { responsiblePersonFields };
};

export default useResponsiblePerson;
